<script setup>
import { cn } from "@/lib/utils";
import { motion } from "motion-v";
import { computed, useSlots } from "vue";

const props = defineProps({
  as: { type: null, required: false, default: "p" },
  class: {
    type: [Boolean, null, String, Object, Array],
    required: false,
    skipCheck: true,
  },
  duration: { type: Number, required: false, default: 2 },
  spread: { type: Number, required: false, default: 2 },
});

const slots = useSlots();

const textContent = computed(() => {
  const defaultSlot = slots.default?.();
  if (!defaultSlot || defaultSlot.length === 0) return "";

  return defaultSlot
    .map((vnode) => {
      if (typeof vnode.children === "string") {
        return vnode.children;
      }
      return "";
    })
    .join("");
});

const dynamicSpread = computed(() => {
  return (textContent.value?.length ?? 0) * props.spread;
});

const componentClasses = computed(() =>
  cn(
    "relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent",
    "[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--color-background),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]",
    props.class,
  ),
);

const componentStyle = computed(() => ({
  "--spread": `${dynamicSpread.value}px`,
  backgroundImage:
    "var(--bg), linear-gradient(var(--color-muted-foreground), var(--color-muted-foreground))",
}));

const MotionComponent = computed(() => {
  return motion[props.as] || motion.p;
});
</script>

<template>
  <component
    :is="MotionComponent"
    :class="componentClasses"
    :style="componentStyle"
    :initial="{ backgroundPosition: '100% center' }"
    :animate="{ backgroundPosition: '0% center' }"
    :transition="{
      repeat: Number.POSITIVE_INFINITY,
      duration,
      ease: 'linear',
    }"
  >
    <slot />
  </component>
</template>
